// Offers list filter bar.
//
// The status dropdown and the search box were the only two controls here for
// a long time, while offerListFilter() on the backend had always also
// accepted product, broker and a created-date range — the comment in that
// function says so out loud. That gap mattered once the batch export grew a
// 50-document ceiling: "narrow the filter" is not advice a user can act on
// when there are only two filters and neither one cuts a 264-offer book down
// to 50. These four controls are the missing half of a selection UI, and
// they send exactly the query keys the server already understood.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { S, Icon } from '../regureStyles';
import { useTenantProducts, productLabel } from '../../utils/productCatalog';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const STATUS_LABEL_KEYS = {
  all:      'offers.list.statusAll',
  draft:    'offers.list.statusDraft',
  sent:     'offers.list.statusSent',
  accepted: 'offers.list.statusAccepted',
  declined: 'offers.list.statusDeclined',
  expired:  'offers.list.statusExpired',
};

const controlStyle = {
  padding: '8px 12px',
  background: '#fff',
  border: `1px solid ${S.borderLight}`,
  borderRadius: 8,
  fontSize: 13,
  color: S.textSecondary,
  cursor: 'pointer',
  outline: 'none',
};

const Filter = ({
  search, setSearch, setPage,
  statusFilter, setStatusFilter,
  productFilter, setProductFilter,
  brokerFilter, setBrokerFilter,
  dateFrom, setDateFrom,
  dateTo, setDateTo,
  clearFilters, hasActiveFilters,
  STATUS_OPTIONS, STATUS_LABELS, filtered,
}) => {
  const { t } = useTranslation();
  const products = useTenantProducts();
  const [brokers, setBrokers] = useState([]);

  // Same call BrokerSelect makes on the offer forms. A tenant with no
  // brokers configured gets no dropdown rather than an empty one that looks
  // like a loading failure.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(
          `${BASE_URL}/brokers?limit=500&status=active`,
          { withCredentials: true },
        );
        if (!cancelled) setBrokers(Array.isArray(resp.data?.data) ? resp.data.data : []);
      } catch {
        /* leave the broker filter out rather than showing a broken control */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Every control resets to page 1: a filter that narrows the book to 12 rows
  // while the table is still on page 4 shows an empty table, which reads as
  // "no results" rather than "wrong page".
  const onChange = (setter) => (value) => { setter(value); setPage(1); };

  return (
    <div
      style={{
        padding: '14px 32px',
        display: 'flex',
        gap: 10,
        alignItems: 'center',
        borderBottom: `1px solid ${S.borderLight}`,
        background: 'rgba(255,255,255,0.6)',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ position: 'relative', flex: 1, minWidth: 220, maxWidth: 320 }}>
        <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
          <Icon name='search' color='#94a3b8' size={15} />
        </div>
        <input
          value={search}
          onChange={e => onChange(setSearch)(e.target.value)}
          placeholder={t('offers.list.searchPlaceholder')}
          style={{
            width: '100%',
            padding: '9px 12px 9px 36px',
            background: '#fff',
            border: `1px solid ${S.borderLight}`,
            borderRadius: 8,
            fontSize: 13,
            color: S.textPrimary,
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </div>

      <select
        value={statusFilter}
        onChange={e => onChange(setStatusFilter)(e.target.value)}
        style={controlStyle}
        aria-label={t('offers.list.colStatus')}
      >
        {STATUS_OPTIONS.map(o => (
          <option key={o} value={o}>
            {STATUS_LABEL_KEYS[o] ? t(STATUS_LABEL_KEYS[o]) : STATUS_LABELS[o]}
          </option>
        ))}
      </select>

      <select
        value={productFilter}
        onChange={e => onChange(setProductFilter)(e.target.value)}
        style={controlStyle}
        aria-label={t('offers.list.colProduct')}
      >
        <option value='all'>{t('offers.list.filterAllProducts')}</option>
        {products.map(code => (
          <option key={code} value={code}>{productLabel(t, code, null, 'short')}</option>
        ))}
      </select>

      {brokers.length > 0 && (
        <select
          value={brokerFilter}
          onChange={e => onChange(setBrokerFilter)(e.target.value)}
          style={controlStyle}
          aria-label={t('offers.list.colBroker')}
        >
          <option value='all'>{t('offers.list.filterAllBrokers')}</option>
          {brokers.map(b => (
            <option key={b._id} value={b._id}>{b.name}</option>
          ))}
        </select>
      )}

      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <Icon name='calendar' color='#94a3b8' size={15} />
        <input
          type='date'
          value={dateFrom}
          max={dateTo || undefined}
          onChange={e => onChange(setDateFrom)(e.target.value)}
          aria-label={t('offers.list.filterDateFrom')}
          title={t('offers.list.filterDateFrom')}
          style={{ ...controlStyle, padding: '7px 10px', cursor: 'text' }}
        />
        <span style={{ fontSize: 12, color: S.textMuted }}>–</span>
        <input
          type='date'
          value={dateTo}
          min={dateFrom || undefined}
          onChange={e => onChange(setDateTo)(e.target.value)}
          aria-label={t('offers.list.filterDateTo')}
          title={t('offers.list.filterDateTo')}
          style={{ ...controlStyle, padding: '7px 10px', cursor: 'text' }}
        />
      </div>

      {hasActiveFilters && (
        <button
          onClick={() => { clearFilters(); setPage(1); }}
          style={{
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '7px 10px', background: 'transparent',
            border: `1px solid ${S.borderLight}`, borderRadius: 8,
            fontSize: 12, fontWeight: 600, color: S.textMuted, cursor: 'pointer',
          }}
        >
          <Icon name='x' color={S.textMuted} size={13} />
          {t('offers.list.filterClear')}
        </button>
      )}

      <span style={{ marginLeft: 'auto', fontSize: 12, color: S.textMuted }}>
        {t('offers.list.resultsCount', { count: filtered.length })}
      </span>
    </div>
  );
};

export default Filter;
